import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { Customer } from '../../customer/customer.model';
import { CustomerService } from '../../customer/customer.service';
import { LocationService } from '../../location/location.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { TransportPlanResourceContext } from '../transport-planning.model';
import {
  ManualTransportPlanRequest,
  TransportExecutionLink,
  TransportOptimizationPlan,
  TransportOptimizationPlanRoute,
  TransportOptimizationPlanRouteStop,
  TransportOptimizationPlanVehicle,
  TransportOrderRelease,
  TransportOrderReleaseLine
} from '../transport-planning.model';
import { TransportPlanningService } from '../transport-planning.service';
import { RentalAssignmentCostPreview } from '../../fleet/rental/rental.model';
import { RentalService } from '../../fleet/rental/rental.service';
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';

interface ManualTruckLane {
  vehicleSnapshotId: number;
  vehicleLabel: string;
  releaseIds: number[];
}

interface RouteDisplayStop {
  displaySequence: number;
  stopType: string;
  locationId?: number;
  locationName: string;
  plannedArrival?: string | Date;
  plannedDeparture?: string | Date;
  latitude?: number;
  longitude?: number;
  releaseIds: number[];
  rawStopCount: number;
}

@Component({
  selector: 'app-transport-plan-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    PageShellComponent,
    PageHeroComponent,
    PageDataPanelComponent
  ],
  templateUrl: './transport-plan-view.component.html',
  styleUrls: ['./transport-plan-view.component.css']
})
export class TransportPlanViewComponent implements OnInit {
  plan?: TransportOptimizationPlan;
  isLoading = false;
  message = '';
  cancelDialogVisible = false;
  cancellationReason = '';
  cancelDialogError = '';
  manualReleaseId?: number | null = null;
  manualTruckId?: number | null = null;
  manualLanes: ManualTruckLane[] = [];
  contactDialogVisible = false;
  contactSearchQuery = '';
  contactSearchResults: Customer[] = [];
  contactSearchLoading = false;
  selectedDefaultContact?: Customer | null = null;
  contactSelectorStop?: RouteDisplayStop | null = null;
  rentalCostPreviews: Record<number, RentalAssignmentCostPreview> = {};

  constructor(
    private route: ActivatedRoute,
    private service: TransportPlanningService,
    private customerService: CustomerService,
    private locationService: LocationService,
    private rentalService: RentalService,
    private permissionService: PermissionService
  ) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    const planId = Number(this.route.snapshot.paramMap.get('planId'));
    if (!planId) return;
    this.isLoading = true;
    this.service.getPlan(planId).subscribe({
      next: plan => {
        this.setPlan(plan);
        this.isLoading = false;
      },
      error: () => {
        this.message = 'Could not open this plan.';
        this.isLoading = false;
      }
    });
  }

  optimize(): void {
    if (this.isBulkPlan()) return;
    if (!this.plan?.planId) return;
    this.isLoading = true;
    this.service.optimizePlan(this.plan.planId).subscribe({
      next: plan => {
        this.setPlan(plan);
        this.message = 'Routes are ready for review.';
        this.isLoading = false;
      },
      error: () => {
        this.message = 'Could not optimize this plan. Check the selected assets and release locations.';
        this.isLoading = false;
      }
    });
  }

  apply(): void {
    if (this.isBulkPlan()) return;
    if (!this.plan?.planId) return;
    if (!this.projectAllocationsReady()) return;
    this.isLoading = true;
    this.service.saveRoutePricing(this.plan.planId, this.routePricingRequest()).subscribe({
      next: plan => {
        this.plan = plan;
        this.service.applyPlan(Number(this.plan.planId)).subscribe({
          next: result => {
            this.message = `Shipments created: ${result.shipmentIds.join(', ')}`;
            this.load();
          },
          error: error => {
            this.message = error?.error?.message || 'Could not create shipments. Check route readiness and confirm the plan is not already applied.';
            this.isLoading = false;
          }
        });
      },
      error: error => {
        this.message = error?.error?.message || 'Could not save route pricing.';
        this.isLoading = false;
      }
    });
  }

  isPlanReadOnly(): boolean {
    return this.plan?.status === 'APPLIED' || this.plan?.status === 'CANCELLED';
  }

  canCancelPlan(): boolean {
    return !this.isBulkPlan()
      && (this.plan?.status === 'DRAFT' || this.plan?.status === 'SOLVED'
        || this.plan?.status === 'STALE' || this.plan?.status === 'PARTIAL')
      && this.permissionService.hasPermission(app_permissions.transport_planning.plans.apply);
  }

  openCancelDialog(): void {
    if (!this.canCancelPlan()) return;
    this.cancellationReason = '';
    this.cancelDialogError = '';
    this.cancelDialogVisible = true;
  }

  cancelPlan(): void {
    if (!this.canCancelPlan() || !this.plan?.planId || this.isLoading) return;
    const reason = this.cancellationReason.trim();
    if (!reason || reason.length > 500) {
      this.cancelDialogError = 'Enter a cancellation reason of no more than 500 characters.';
      return;
    }
    this.cancelDialogError = '';
    this.isLoading = true;
    this.service.cancelPlan(this.plan.planId, reason).subscribe({
      next: plan => {
        this.setPlan(plan);
        this.cancelDialogVisible = false;
        this.message = 'Plan cancelled. Its routes no longer reserve drivers or trucks.';
        this.isLoading = false;
      },
      error: error => {
        this.cancelDialogError = error?.error?.message || 'Could not cancel this plan. Refresh and try again.';
        this.isLoading = false;
      }
    });
  }

  saveRoutePricing(): void {
    if (this.isBulkPlan()) return;
    if (!this.plan?.planId) return;
    if (!this.projectAllocationsReady()) return;
    this.isLoading = true;
    this.service.saveRoutePricing(this.plan.planId, this.routePricingRequest()).subscribe({
      next: plan => {
        this.setPlan(plan);
        this.message = 'Route pricing saved.';
        this.isLoading = false;
      },
      error: error => {
        this.message = error?.error?.message || 'Could not save route pricing.';
        this.isLoading = false;
      }
    });
  }

  saveManualMatches(): void {
    if (this.isBulkPlan()) return;
    if (!this.plan?.planId) return;
    const missing = this.unmatchedReleases();
    if (missing.length > 0) {
      this.message = `Assign ${missing.length} load${missing.length === 1 ? '' : 's'} before saving the manual match.`;
      return;
    }
    const request: ManualTransportPlanRequest = {
      routes: this.manualLanes
        .filter(lane => lane.releaseIds.length > 0)
        .map(lane => ({
          vehicleSnapshotId: lane.vehicleSnapshotId,
          releaseIds: lane.releaseIds
        }))
    };
    if (request.routes.length === 0) {
      this.message = 'Assign at least one load to a truck before saving.';
      return;
    }
    this.isLoading = true;
    this.service.saveManualRoutes(this.plan.planId, request).subscribe({
      next: plan => {
        this.setPlan(plan);
        this.message = 'Manual load and truck matching saved. Review the routes, then create shipments.';
        this.isLoading = false;
      },
      error: error => {
        this.message = error?.error?.message || 'Could not save the manual truck match.';
        this.isLoading = false;
      }
    });
  }

  assignManualLoad(): void {
    if (!this.manualReleaseId || !this.manualTruckId) {
      this.message = 'Choose one load and one truck first.';
      return;
    }
    const lane = this.manualLanes.find(item => item.vehicleSnapshotId === this.manualTruckId);
    if (!lane) {
      this.message = 'Choose a ready truck.';
      return;
    }
    this.manualLanes.forEach(item => {
      item.releaseIds = item.releaseIds.filter(releaseId => releaseId !== this.manualReleaseId);
    });
    lane.releaseIds.push(this.manualReleaseId);
    this.manualReleaseId = this.unmatchedReleases()[0]?.releaseId ?? null;
  }

  removeManualLoad(lane: ManualTruckLane, releaseId?: number): void {
    if (!releaseId) return;
    lane.releaseIds = lane.releaseIds.filter(id => id !== releaseId);
    this.manualReleaseId = releaseId;
  }

  clearLane(lane: ManualTruckLane): void {
    lane.releaseIds = [];
    this.manualReleaseId = this.unmatchedReleases()[0]?.releaseId ?? null;
  }

  scrollToManualWorkbench(): void {
    document.getElementById('manual-workbench')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  date(value?: string | Date): string {
    return value ? formatDate(value, 'dd MMM yyyy HH:mm', 'en-US') : 'Not set';
  }

  distance(route?: TransportOptimizationPlanRoute): string {
    return route?.totalDistanceMeters ? `${(route.totalDistanceMeters / 1000).toFixed(1)} km` : 'Distance pending';
  }

  duration(seconds?: number): string {
    return seconds ? `${(seconds / 3600).toFixed(1)} hours` : 'Time pending';
  }

  statusSeverity(status?: string): string {
    if (status === 'APPLIED') return 'success';
    if (status === 'CANCELLED') return 'secondary';
    if (status === 'SOLVED') return 'info';
    if (status === 'STALE') return 'warn';
    if (status === 'FAILED' || status === 'VALIDATION_ERROR') return 'danger';
    return 'secondary';
  }

  stateIcon(): string {
    if (this.plan?.status === 'CANCELLED') return 'pi pi-ban';
    if (this.plan?.stale) return 'pi pi-exclamation-triangle';
    if (this.plan?.status === 'APPLIED') return 'pi pi-check-circle';
    if (this.isManualPlan()) return 'pi pi-user-edit';
    if (this.hasProviderFallback()) return 'pi pi-exclamation-triangle';
    if (this.plan?.status === 'SOLVED') return 'pi pi-map';
    return 'pi pi-truck';
  }

  stateTitle(): string {
    if (this.plan?.status === 'CANCELLED') return 'Plan cancelled';
    if (this.plan?.stale) return 'Plan is stale';
    if (this.plan?.status === 'APPLIED') return 'Shipments created';
    if (this.isManualPlan()) return 'Manual truck matching is ready';
    if (this.hasProviderFallback()) return 'Simple route plan is ready';
    if (this.plan?.status === 'SOLVED') return 'Routes are ready';
    return 'Ready to arrange trucks';
  }

  stateMessage(): string {
    if (this.plan?.status === 'CANCELLED') {
      return 'The planned routes no longer reserve drivers or trucks. The releases can be planned again.';
    }
    if (this.plan?.stale) {
      return this.plan.conflictMessage || 'One or more releases were already applied by another planning flow.';
    }
    if (this.plan?.status === 'APPLIED') {
      return 'This truck plan has already been converted into shipments.';
    }
    if (this.isManualPlan()) {
      return 'The load-to-truck assignments were saved manually and are ready for review.';
    }
    if (this.hasProviderFallback()) {
      return 'Google optimization could not run, so LogiLink created a simple stop sequence for review.';
    }
    if (this.plan?.status === 'SOLVED') {
      return 'Review the suggested routes, then create shipments when they look correct.';
    }
    return 'Click Arrange Trucks to calculate suggested routes from the selected work and assigned assets.';
  }

  issueTitle(): string | null {
    if (this.plan?.status === 'CANCELLED') return null;
    if (this.plan?.stale) return 'Release planning conflict';
    if (this.hasProviderFallback()) return 'Google optimizer was skipped';
    if (this.plan?.validationSummary) return 'Review needed';
    return null;
  }

  issueMessage(): string | null {
    if (this.plan?.stale) return this.plan.conflictMessage || 'Create a new plan using the remaining open releases.';
    if (this.hasProviderFallback()) {
      return 'The fallback route is usable for review, but distance and drive-time numbers may be unavailable.';
    }
    return this.plan?.validationSummary || null;
  }

  manualProgressText(): string {
    return `${this.matchedLoadCount()} of ${this.plan?.releases?.length || 0} loads matched`;
  }

  manualLoadOptions(): { label: string; value: number }[] {
    return this.unmatchedReleases().map(release => ({
      label: `${this.customerLabel(release)} - ${this.releaseLabel(release)} - ${this.releaseRoute(release)}`,
      value: Number(release.releaseId)
    }));
  }

  manualTruckOptions(): { label: string; value: number }[] {
    return this.manualLanes.map(lane => ({
      label: lane.vehicleLabel,
      value: lane.vehicleSnapshotId
    }));
  }

  laneVehicle(lane: ManualTruckLane): TransportOptimizationPlanVehicle | undefined {
    return (this.plan?.vehicles || []).find(vehicle => vehicle.vehicleSnapshotId === lane.vehicleSnapshotId);
  }

  unmatchedReleases(): TransportOrderRelease[] {
    const assigned = new Set<number>();
    this.manualLanes.forEach(lane => lane.releaseIds.forEach(id => assigned.add(id)));
    return (this.plan?.releases || []).filter(release => !!release.releaseId && !assigned.has(Number(release.releaseId)));
  }

  assignedReleases(lane: ManualTruckLane): TransportOrderRelease[] {
    return lane.releaseIds
      .map(releaseId => (this.plan?.releases || []).find(release => release.releaseId === releaseId))
      .filter((release): release is TransportOrderRelease => !!release);
  }

  releaseLabel(release?: TransportOrderRelease): string {
    if (!release) return 'Load';
    return release.releaseNumber || release.sourceNumber || `Release ${release.releaseId}`;
  }

  releaseRoute(release?: TransportOrderRelease): string {
    if (!release) return 'Route not set';
    return `${release.originName || 'Start not set'} to ${release.destinationName || 'End not set'}`;
  }

  releaseLoad(release?: TransportOrderRelease): string {
    const lines = release?.lines || [];
    if (lines.length === 0) return 'Load details not set';
    const quantity = lines.reduce((sum, line) => sum + Number(line.quantity ?? line.units ?? 0), 0);
    const uoms = Array.from(new Set(lines.map(line => line.uom).filter(Boolean)));
    const quantityText = quantity > 0
      ? `${this.formatLoadAmount(quantity)} ${uoms.length === 1 ? uoms[0] : 'units'}`
      : `${lines.length} line${lines.length === 1 ? '' : 's'}`;
    const first = this.lineTitle(lines[0]);
    return lines.length === 1 ? `${quantityText} - ${first}` : `${quantityText} across ${lines.length} lines - ${first}`;
  }

  customerLabel(release?: TransportOrderRelease): string {
    if (!release) return 'Customer missing';
    return release.crmClientName
      || release.companyName
      || release.customerName
      || (release.crmClientId ? `CRM Client #${release.crmClientId}` : '')
      || (release.companyId ? `Company #${release.companyId}` : '')
      || (release.customerId ? `Customer #${release.customerId}` : '')
      || 'Customer missing';
  }

  accountLabel(release?: TransportOrderRelease): string {
    if (!release) return '';
    return release.crmAccountName || (release.crmAccountId ? `Account #${release.crmAccountId}` : '');
  }

  stopCustomer(releaseId?: number): string {
    const release = (this.plan?.releases || []).find(row => row.releaseId === releaseId);
    return release ? this.customerLabel(release) : '';
  }

  routeDisplayStops(route?: TransportOptimizationPlanRoute): RouteDisplayStop[] {
    const stops = [...(route?.stops || [])]
      .sort((left, right) => Number(left.stopSequence ?? 0) - Number(right.stopSequence ?? 0));
    const grouped = new Map<string, RouteDisplayStop>();

    for (const stop of stops) {
      const key = this.routeStopKey(stop);
      let row = grouped.get(key);
      if (!row) {
        row = {
          displaySequence: grouped.size + 1,
          stopType: this.formatStopType(stop.stopType),
          locationId: stop.locationId,
          locationName: stop.locationName || 'Location not set',
          plannedArrival: stop.plannedArrival,
          plannedDeparture: stop.plannedDeparture,
          latitude: stop.latitude,
          longitude: stop.longitude,
          releaseIds: [],
          rawStopCount: 0
        };
        grouped.set(key, row);
      }
      row.rawStopCount += 1;
      row.locationId = row.locationId ?? stop.locationId;
      if (stop.releaseId && !row.releaseIds.includes(Number(stop.releaseId))) {
        row.releaseIds.push(Number(stop.releaseId));
      }
      row.plannedArrival = this.earlierDate(row.plannedArrival, stop.plannedArrival);
      row.plannedDeparture = this.laterDate(row.plannedDeparture, stop.plannedDeparture);
    }

    return Array.from(grouped.values()).map((stop, index) => ({ ...stop, displaySequence: index + 1 }));
  }

  routeRawStopCount(route?: TransportOptimizationPlanRoute): number {
    return route?.stops?.length || 0;
  }

  routePricingRequest() {
    return {
      routes: (this.plan?.routes || [])
        .filter(route => !!route.routeId)
        .map(route => ({
          routeId: route.routeId,
          buyingPrice: this.nullableMoney(route.buyingPrice),
          sellingPrice: route.billingMode === 'CONSOLIDATED_ZERO_SELL' ? 0 : this.nullableMoney(route.sellingPrice),
          projectAllocations: (route.projectAllocations || []).some(allocation => allocation.manualOverride)
            ? (route.projectAllocations || []).map(allocation => ({
                logisticsProjectId: allocation.logisticsProjectId,
                allocationPercentage: Number(allocation.allocationPercentage || 0)
              }))
            : []
        }))
    };
  }

  routeMargin(route?: TransportOptimizationPlanRoute): string {
    if (route?.billingMode === 'CONSOLIDATED_ZERO_SELL') return 'No selling or receivable';
    const selling = Number(route?.sellingPrice ?? 0);
    const buying = Number(route?.buyingPrice ?? 0);
    if (!selling && !buying) return 'No pricing entered';
    return `Margin ${this.money(selling - buying)}`;
  }

  routeOperationalCostLabel(route?: TransportOptimizationPlanRoute): string {
    return this.routeUsesInternalCost(route) ? 'Internal cost' : 'Buying price';
  }

  routePricingHint(route?: TransportOptimizationPlanRoute): string {
    return this.routeUsesInternalCost(route)
      ? 'Owned and sister-fleet routes populate shipment internal cost.'
      : 'Freelance, rental, and transporter routes populate shipment buying price.';
  }

  isMixedClientOrProjectRoute(route?: TransportOptimizationPlanRoute): boolean {
    const releases = this.routeReleases(route);
    if (releases.length <= 1) {
      return false;
    }
    const projectKeys = new Set(
      releases
        .map(release => release.logisticsProjectId)
        .filter(value => value != null)
        .map(value => String(value))
    );
    const partyKeys = new Set(
      releases
        .map(release => this.releasePartyKey(release))
        .filter(Boolean)
    );
    return projectKeys.size > 1 || partyKeys.size > 1;
  }

  mixedRouteNotice(route?: TransportOptimizationPlanRoute): string {
    if (route?.billingMode === 'CONSOLIDATED_ZERO_SELL')
      return 'Shared shipment for multiple bill-to parties. Selling is zero; costs remain allocated to projects.';
    return this.isMixedClientOrProjectRoute(route)
      ? `Consolidated route: buying, internal cost, and selling are allocated across ${route?.projectAllocations?.length || 0} projects.`
      : '';
  }

  allocationTotal(route?: TransportOptimizationPlanRoute): number {
    return (route?.projectAllocations || []).reduce(
      (total, allocation) => total + Number(allocation.allocationPercentage || 0), 0);
  }

  allocationTotalValid(route?: TransportOptimizationPlanRoute): boolean {
    return Math.abs(this.allocationTotal(route) - 100) < 0.0001;
  }

  private projectAllocationsReady(): boolean {
    const invalid = (this.plan?.routes || []).find(route =>
      (route.projectAllocations?.length || 0) > 0 && !this.allocationTotalValid(route));
    if (!invalid) return true;
    this.message = `Route ${invalid.routeSequence || invalid.routeId}: project allocation must total exactly 100%.`;
    return false;
  }

  resetProjectAllocations(route: TransportOptimizationPlanRoute): void {
    route.projectAllocations = [];
    this.saveRoutePricing();
  }

  projectOperationalAllocation(
    route: TransportOptimizationPlanRoute,
    allocation: NonNullable<TransportOptimizationPlanRoute['projectAllocations']>[number]
  ): number {
    const source = this.routeBuyingSource(route);
    const internal = source === 'INTERNAL_TRANSPORT_COST'
      || source === 'SISTER_INTERNAL_TRANSPORT_COST'
      || source === 'CUSTOM_INTERNAL_TRANSPORT_COST';
    return internal
      ? Number(allocation.allocatedInternalCost || 0)
      : Number(allocation.allocatedBuying || 0);
  }

  allocatedMoney(value?: number): string {
    return this.money(Number(value || 0));
  }

  routeBuyingSourceLabel(route?: TransportOptimizationPlanRoute): string {
    switch (this.routeBuyingSource(route)) {
      case 'INTERNAL_TRANSPORT_COST':
        return 'Internal cost';
      case 'SISTER_INTERNAL_TRANSPORT_COST':
        return 'Sister internal cost';
      case 'CUSTOM_INTERNAL_TRANSPORT_COST':
        return 'Custom internal cost';
      case 'SUPPLIER_DELIVERY_RATE_CARD':
        return 'Supplier rate card';
      case 'MANUAL':
        return 'Manual';
      case 'NO_MATCH':
        return 'No match';
      default:
        return 'Pricing pending';
    }
  }

  routeBuyingSourceSeverity(route?: TransportOptimizationPlanRoute): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (this.routeBuyingSource(route)) {
      case 'INTERNAL_TRANSPORT_COST':
      case 'SISTER_INTERNAL_TRANSPORT_COST':
      case 'CUSTOM_INTERNAL_TRANSPORT_COST':
        return 'success';
      case 'SUPPLIER_DELIVERY_RATE_CARD':
        return 'info';
      case 'MANUAL':
        return 'warn';
      case 'NO_MATCH':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  routeBuyingSourceDetail(route?: TransportOptimizationPlanRoute): string {
    if (!route) return '';
    const source = this.routeBuyingSource(route);
    if (source === 'MANUAL') {
      const recommended = route.recommendedBuyingPrice ?? route.buyingPriceRecommendation?.recommendedBuyingPrice;
      return recommended != null ? `Recommended ${this.money(Number(recommended))}` : 'Edited in transport plan';
    }
    if (source === 'NO_MATCH') {
      return route.pricingWarning || route.buyingPriceRecommendation?.warnings?.[0] || 'Enter buying price manually';
    }
    if (source === 'SUPPLIER_DELIVERY_RATE_CARD' && route.pricingRateCardId) {
      return `Rate card #${route.pricingRateCardId}`;
    }
    if ((source === 'INTERNAL_TRANSPORT_COST' || source === 'SISTER_INTERNAL_TRANSPORT_COST')
      && route.pricingInternalCostCardId) {
      return `Cost card #${route.pricingInternalCostCardId}`;
    }
    return route.pricingWarning || '';
  }

  private routeUsesInternalCost(route?: TransportOptimizationPlanRoute): boolean {
    const ownershipType = (route?.vehicleContext?.resourceOwnershipType || '').trim().toUpperCase();
    return ownershipType !== 'FREELANCE' && ownershipType !== 'RENTAL' && ownershipType !== 'TRANSPORTER';
  }

  private routeBuyingSource(route?: TransportOptimizationPlanRoute): string {
    return (route?.buyingPriceSource
      || route?.buyingPriceRecommendation?.buyingPriceSource
      || route?.buyingPriceRecommendation?.recommendationSource
      || '').toUpperCase();
  }

  routeReleaseCount(route?: TransportOptimizationPlanRoute): number {
    return this.routeReleaseIds(route).size;
  }

  routeStopCustomers(stop: RouteDisplayStop): string {
    return this.uniqueReleaseValues(stop.releaseIds, release => this.customerLabel(release)).join(' / ');
  }

  routeStopReleaseLabels(stop: RouteDisplayStop): string {
    return this.uniqueReleaseValues(stop.releaseIds, release => this.releaseLabel(release)).join(', ');
  }

  routeStopReleases(stop: RouteDisplayStop): TransportOrderRelease[] {
    return stop.releaseIds
      .map(releaseId => (this.plan?.releases || []).find(release => release.releaseId === releaseId))
      .filter((release): release is TransportOrderRelease => !!release);
  }

  private routeReleases(route?: TransportOptimizationPlanRoute): TransportOrderRelease[] {
    const releaseIds = this.routeReleaseIds(route);
    return (this.plan?.releases || []).filter(release => !!release.releaseId && releaseIds.has(Number(release.releaseId)));
  }

  private routeReleaseIds(route?: TransportOptimizationPlanRoute): Set<number> {
    const releaseIds = new Set<number>();
    (route?.stops || []).forEach(stop => {
      if (stop.releaseId) releaseIds.add(Number(stop.releaseId));
    });
    return releaseIds;
  }

  private releasePartyKey(release: TransportOrderRelease): string {
    if (release.crmClientId != null) {
      return `CRM:C:${release.crmClientId}|A:${release.crmAccountId ?? '-'}|T:${release.crmClientContactId ?? '-'}`;
    }
    if (release.customerId != null || release.companyId != null) {
      return `LEGACY:P:${release.companyId ?? '-'}|K:${release.customerId ?? '-'}`;
    }
    return '';
  }

  routeStopLoadSummary(stop: RouteDisplayStop): string {
    const releases = stop.releaseIds
      .map(releaseId => (this.plan?.releases || []).find(release => release.releaseId === releaseId))
      .filter((release): release is TransportOrderRelease => !!release);
    if (releases.length === 0) return '';
    const lineCount = releases.reduce((sum, release) => sum + (release.lines?.length || 0), 0);
    const totalQuantity = releases
      .flatMap(release => release.lines || [])
      .reduce((sum, line) => sum + Number(line.quantity ?? line.units ?? 0), 0);
    const uoms = Array.from(new Set(releases.flatMap(release => (release.lines || []).map(line => line.uom).filter(Boolean))));
    const quantity = totalQuantity > 0
      ? `${this.formatLoadAmount(totalQuantity)} ${uoms.length === 1 ? uoms[0] : 'units'}`
      : `${lineCount} line${lineCount === 1 ? '' : 's'}`;
    return `${quantity} across ${releases.length} release${releases.length === 1 ? '' : 's'}`;
  }

  routeStopMapUrl(stop: RouteDisplayStop): string | null {
    return stop.latitude != null && stop.longitude != null
      ? `https://maps.google.com?q=${stop.latitude},${stop.longitude}`
      : null;
  }

  routeStopNeedsCustomer(stop: RouteDisplayStop): boolean {
    return this.routeStopCustomers(stop)
      .split('/')
      .map(value => value.trim().toLowerCase())
      .some(value => value === 'customer missing');
  }

  routeStopSegment(route: TransportOptimizationPlanRoute, stop: RouteDisplayStop): string {
    if (stop.displaySequence <= 1) return 'Route start';
    const stops = this.routeDisplayStops(route);
    const previous = stops.find(candidate => candidate.displaySequence === stop.displaySequence - 1);
    if (!previous) return 'Segment unavailable';
    if (stops.length === 2 && route.totalDistanceMeters && route.totalDurationSeconds) {
      return `${(route.totalDistanceMeters / 1000).toFixed(1)} km / ${this.durationMinutes(route.totalDurationSeconds / 60)} from previous stop`;
    }
    const distanceKm = this.geoDistanceKm(previous, stop);
    if (distanceKm == null) return 'Distance/time unavailable';
    const minutes = (distanceKm / 45) * 60;
    return `~${distanceKm.toFixed(1)} km / ~${this.durationMinutes(minutes)} from previous stop`;
  }

  openDefaultContactDialog(stop: RouteDisplayStop): void {
    if (!stop.locationId) {
      this.message = 'This stop is not linked to a saved location.';
      return;
    }
    this.contactSelectorStop = stop;
    this.contactDialogVisible = true;
    this.contactSearchQuery = '';
    this.contactSearchResults = [];
    this.selectedDefaultContact = null;
  }

  searchRouteContacts(): void {
    const query = this.contactSearchQuery.trim();
    if (query.length < 2) {
      this.contactSearchResults = [];
      return;
    }
    this.contactSearchLoading = true;
    this.customerService.searchCustomers(query, 0, 25).subscribe({
      next: contacts => {
        this.contactSearchResults = contacts || [];
        this.contactSearchLoading = false;
      },
      error: () => {
        this.contactSearchResults = [];
        this.contactSearchLoading = false;
        this.message = 'Could not search contacts.';
      }
    });
  }

  selectDefaultContact(contact: Customer): void {
    this.selectedDefaultContact = contact;
  }

  saveDefaultContact(): void {
    const locationId = this.contactSelectorStop?.locationId;
    const contactId = this.selectedDefaultContact?.id;
    if (!locationId || !contactId) {
      this.message = 'Select a contact before saving the location default contact.';
      return;
    }
    this.isLoading = true;
    this.locationService.updateDefaultContact(locationId, contactId).subscribe({
      next: response => {
        this.contactDialogVisible = false;
        this.message = response?.success
          ? 'Location default contact updated. Release validation has been refreshed.'
          : response?.message || 'Location default contact update returned no confirmation.';
        this.load();
      },
      error: error => {
        this.message = error?.error?.message || 'Could not update the location default contact.';
        this.isLoading = false;
      }
    });
  }

  contactLabel(contact?: Customer | null): string {
    if (!contact) return 'Contact';
    return [contact.name, contact.phoneNumber, contact.emailAddress].filter(Boolean).join(' / ') || `Contact #${contact.id}`;
  }

  private lineTitle(line: TransportOrderReleaseLine): string {
    return [line.itemCode, line.itemName || line.itemDescription]
      .filter(Boolean)
      .join(' - ') || 'Transport demand';
  }

  private formatLoadAmount(value: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
  }

  private nullableMoney(value: number | null | undefined): number | null {
    return value === null || value === undefined ? null : Number(value);
  }

  private money(value: number): string {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'SAR' }).format(value);
  }

  private durationMinutes(minutes: number): string {
    if (!Number.isFinite(minutes) || minutes <= 0) return 'time unavailable';
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const hours = Math.floor(minutes / 60);
    const remainder = Math.round(minutes % 60);
    return remainder > 0 ? `${hours}h ${remainder}m` : `${hours}h`;
  }

  private geoDistanceKm(left: RouteDisplayStop, right: RouteDisplayStop): number | null {
    if (left.latitude == null || left.longitude == null || right.latitude == null || right.longitude == null) {
      return null;
    }
    const radiusKm = 6371;
    const lat1 = this.toRadians(Number(left.latitude));
    const lat2 = this.toRadians(Number(right.latitude));
    const deltaLat = this.toRadians(Number(right.latitude) - Number(left.latitude));
    const deltaLng = this.toRadians(Number(right.longitude) - Number(left.longitude));
    const haversine = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2)
      + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    return radiusKm * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  }

  private toRadians(value: number): number {
    return value * Math.PI / 180;
  }

  private routeStopKey(stop: TransportOptimizationPlanRouteStop): string {
    const type = this.formatStopType(stop.stopType);
    if (stop.locationId) return `${type}:location:${stop.locationId}`;
    if (stop.latitude != null && stop.longitude != null) {
      return `${type}:geo:${Number(stop.latitude).toFixed(5)},${Number(stop.longitude).toFixed(5)}`;
    }
    return `${type}:name:${(stop.locationName || '').trim().toLowerCase()}`;
  }

  private formatStopType(type?: string): string {
    return (type || 'STOP').trim().toUpperCase();
  }

  private uniqueReleaseValues(releaseIds: number[], mapper: (release: TransportOrderRelease) => string): string[] {
    const values = releaseIds
      .map(releaseId => (this.plan?.releases || []).find(release => release.releaseId === releaseId))
      .filter((release): release is TransportOrderRelease => !!release)
      .map(mapper)
      .filter(Boolean);
    return Array.from(new Set(values));
  }

  private earlierDate(left?: string | Date, right?: string | Date): string | Date | undefined {
    if (!left) return right;
    if (!right) return left;
    return new Date(left).getTime() <= new Date(right).getTime() ? left : right;
  }

  private laterDate(left?: string | Date, right?: string | Date): string | Date | undefined {
    if (!left) return right;
    if (!right) return left;
    return new Date(left).getTime() >= new Date(right).getTime() ? left : right;
  }

  canSaveManualMatches(): boolean {
    return !this.isPlanReadOnly()
      && (this.plan?.releases?.length || 0) > 0
      && this.unmatchedReleases().length === 0
      && this.manualLanes.some(lane => lane.releaseIds.length > 0);
  }

  technicalDetails(): string | null {
    if (!this.hasProviderFallback()) return null;
    return this.plan?.errorMessage || this.plan?.validationSummary || null;
  }

  shipmentLinks(): TransportExecutionLink[] {
    const seen = new Set<number>();
    return (this.plan?.executionLinks ?? []).filter(link => {
      if (!link.shipmentId || seen.has(link.shipmentId)) return false;
      seen.add(link.shipmentId);
      return true;
    });
  }

  shipmentLinkLabel(link: TransportExecutionLink): string {
    return link.shipmentNumber || (link.shipmentId ? `Shipment #${link.shipmentId}` : 'Shipment');
  }

  resourceOwnershipTypeLabel(context?: TransportPlanResourceContext | null): string {
    switch ((context?.resourceOwnershipType || '').toUpperCase()) {
      case 'OWNED':
        return 'Owned';
      case 'FREELANCE':
        return 'Freelance';
      case 'RENTAL':
        return 'Rental';
      case 'TRANSPORTER':
        return 'Transporter';
      default:
        return 'External';
    }
  }

  resourceOwnerLine(context?: TransportPlanResourceContext | null): string {
    if (!context) {
      return '-';
    }
    return `${this.resourceOwnershipTypeLabel(context)} | ${context.ownerLabel || context.resourceCompanyName || '-'}`;
  }

  resourceAgencySourceLine(context?: TransportPlanResourceContext | null): string {
    if (!context) {
      return '-';
    }
    const sourceCompany = context.resourceCompanyName
      && context.resourceCompanyName !== context.ownerLabel
      && context.resourceCompanyName !== context.agencyLabel
      ? context.resourceCompanyName
      : null;
    return [context.agencyLabel, sourceCompany].filter(Boolean).join(' | ') || '-';
  }

  rentalCostLabel(vehicle: TransportOptimizationPlanVehicle): string {
    const preview = vehicle.vehicleSnapshotId == null ? undefined : this.rentalCostPreviews[vehicle.vehicleSnapshotId];
    if (!preview?.lines?.some(line => line.status === 'ELIGIBLE')) return 'Not a daily rental';
    return `${preview.estimatedCost ?? 0} ${preview.currencyCode || ''}`.trim();
  }

  private setPlan(plan: TransportOptimizationPlan): void {
    this.plan = plan;
    this.loadRentalCostPreviews();
    this.resetManualWorkbench();
  }

  private loadRentalCostPreviews(): void {
    this.rentalCostPreviews = {};
    if (!this.plan?.owningCompanyId || !this.plan.horizonStart || !this.plan.horizonEnd) return;
    const fromDate = formatDate(this.plan.horizonStart, 'yyyy-MM-dd', 'en-US');
    const toDate = formatDate(this.plan.horizonEnd, 'yyyy-MM-dd', 'en-US');
    (this.plan.vehicles || []).filter(vehicle => vehicle.vehicleSnapshotId != null).forEach(vehicle => {
      this.rentalService.previewAssignmentCost(this.plan!.owningCompanyId, vehicle.vehicleId, vehicle.driverId, fromDate, toDate).subscribe({
        next: response => { if (vehicle.vehicleSnapshotId != null) this.rentalCostPreviews[vehicle.vehicleSnapshotId] = response.data; },
        error: () => undefined
      });
    });
  }

  private resetManualWorkbench(): void {
    const routes = this.plan?.routes || [];
    this.manualLanes = (this.plan?.vehicles || [])
      .filter(vehicle => !!vehicle.vehicleSnapshotId && vehicle.validationStatus !== 'ERROR')
      .map(vehicle => {
        const releaseIds = new Set<number>();
        routes
          .filter(route => route.vehicleSnapshotId === vehicle.vehicleSnapshotId)
          .forEach(route => (route.stops || []).forEach(stop => {
            if (stop.releaseId) releaseIds.add(Number(stop.releaseId));
          }));
        return {
          vehicleSnapshotId: Number(vehicle.vehicleSnapshotId),
          vehicleLabel: this.vehicleLabel(vehicle),
          releaseIds: Array.from(releaseIds)
        };
      });
    this.manualTruckId = this.manualLanes[0]?.vehicleSnapshotId ?? null;
    this.manualReleaseId = this.unmatchedReleases()[0]?.releaseId ?? null;
  }

  private vehicleLabel(vehicle: TransportOptimizationPlanVehicle): string {
    return [
      vehicle.vehiclePlateNumber,
      vehicle.vehicleRegistrationNumber,
      vehicle.driverName,
      vehicle.vehicleContext?.resourceCompanyName
    ]
      .filter(value => !!value)
      .join(' / ') || vehicle.providerVehicleLabel || `Truck ${vehicle.vehicleSnapshotId}`;
  }

  private matchedLoadCount(): number {
    const assigned = new Set<number>();
    this.manualLanes.forEach(lane => lane.releaseIds.forEach(id => assigned.add(id)));
    return assigned.size;
  }

  private isManualPlan(): boolean {
    const text = `${this.plan?.provider || ''}\n${this.plan?.validationSummary || ''}`.toLowerCase();
    return text.includes('manual_workbench') || text.includes('manual load and truck matching');
  }

  isBulkPlan(): boolean {
    return this.plan?.planningMode === 'BULK'
      || this.plan?.sourceType === 'BULK_PLANNING_RUN'
      || !!this.plan?.bulkPlanningRunId;
  }

  private hasProviderFallback(): boolean {
    const text = `${this.plan?.validationSummary || ''}\n${this.plan?.errorMessage || ''}`.toLowerCase();
    return text.includes('google route optimization')
      || text.includes('invalid json payload')
      || text.includes('fallback solution')
      || text.includes('unknown name');
  }
}
